/**
 * Theme Constants for TimeSlots
 *
 * Design system constants for the timeslot selection UI.
 * These values match the existing MIA-Dashboard branding and design system.
 *
 * Requirements: 1.3, 1.4, 1.5, 6.2, 6.3
 */

/**
 * Color palette for the timeslot selection UI
 */
export const COLORS = {
  // Primary colors - used for selected state and accents
  primary: '#EB7C0C',                         // Orange - selected state, accents
  primaryLight: 'rgba(235, 124, 12, 0.15)',   // Highlighted during drag
  primaryBorder: 'rgba(235, 124, 12, 0.25)',  // Selection box border

  // Status colors - used to indicate slot availability
  statusOpen: '#33B897',      // Green - available slots (Requirement 1.3)
  statusBlocked: '#A4A4A4',   // Grey - time-off (Requirement 1.4)
  statusBooked: '#F43E51',    // Red - appointments (Requirement 1.5)

  // General UI colors
  background: '#FFFFFF',      // Default background
  text: '#1a1a2e',            // Primary text color
  border: '#E8E8E8',          // Default border color

  // Focus state
  focusRing: '#4A90E2',       // Blue focus ring for keyboard navigation (Requirement 6.4)
} as const

/**
 * Font families used in the timeslot UI
 */
export const FONTS = {
  primary: 'coresanscr45',    // Regular weight font
  bold: 'coresanscr65',       // Bold weight font
} as const

/**
 * Animation timing constants
 * Requirement 6.5: 150ms ease-in-out timing for state transitions
 */
export const TRANSITIONS = {
  default: '150ms ease-in-out',
  fast: '100ms ease-in-out',
} as const

/**
 * Selection box styling for drag selection
 * Requirement 3.7: Selection box styled with primary orange at 25% opacity with 2px solid border
 */
export const SELECTION_BOX_STYLE = {
  backgroundColor: COLORS.primaryBorder,
  border: `2px solid ${COLORS.primary}`,
  borderRadius: '4px',
  zIndex: 9999,
  pointerEvents: 'none' as const,
} as const

/**
 * Hover transform scale for open slots
 * Requirement 6.1: Subtle scale transform (1.02) on hover
 */
export const HOVER_SCALE = 1.02

/**
 * Focus ring width for keyboard navigation
 * Requirement 6.4: 3px width focus ring
 */
export const FOCUS_RING_WIDTH = '3px'

// Type exports for consumers
export type Colors = typeof COLORS
export type Fonts = typeof FONTS
export type Transitions = typeof TRANSITIONS
